import { format, formatDistance, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import { utcToZonedTime } from 'date-fns-tz'

export const formatDate = (date: string | Date, pattern: string = 'dd.MM.yyyy'): string => {
  // Parse the date - assuming it's in UTC if it's a string
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  
  // Convert UTC to user's local timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const zonedDate = utcToZonedTime(dateObj, userTimezone)
  
  return format(zonedDate, pattern, { locale: de })
}

export const formatDateTime = (date: string | Date): string => {
  return formatDate(date, 'dd.MM.yyyy HH:mm')
}

// Format UTC date to local timezone
export const formatUTCToLocal = (date: string | Date, pattern: string = 'dd.MM.yyyy HH:mm'): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const zonedDate = utcToZonedTime(dateObj, userTimezone)
  return format(zonedDate, pattern, { locale: de })
}

// Get local timezone info for display
export const getLocalTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const formatTime = (date: string | Date): string => {
  return formatDate(date, 'HH:mm')
}

export const formatRelativeTime = (date: string | Date): string => {
  // Parse the date - assuming it's in UTC if it's a string
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  
  // Convert UTC to user's local timezone for accurate relative time
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const zonedDate = utcToZonedTime(dateObj, userTimezone)
  
  return formatDistance(zonedDate, new Date(), { 
    addSuffix: true,
    locale: de 
  })
}

export const formatNumber = (value: number, decimals: number = 0): string => {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${formatNumber(value, decimals)}%`
}

export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  
  if (hours === 0) {
    return `${mins} min`
  }
  
  return `${hours}h ${mins}m`
}

export const formatOEE = (value?: number): string => {
  if (value === undefined || value === null) {
    return 'N/A'
  }
  return formatPercentage(value, 1)
}

export const formatFileSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  if (bytes === 0) return '0 Bytes'
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

export const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'online':
    case 'running':
    case 'healthy':
      return 'text-green-600 bg-green-100'
    case 'offline':
    case 'stopped':
    case 'error':
    case 'unhealthy':
      return 'text-red-600 bg-red-100'
    case 'maintenance':
    case 'warning':
    case 'degraded':
      return 'text-yellow-600 bg-yellow-100'
    case 'idle':
    case 'unknown':
      return 'text-gray-600 bg-gray-100'
    default:
      return 'text-blue-600 bg-blue-100'
  }
}

export const getOEEColor = (oee?: number): string => {
  if (!oee) return 'text-gray-500'
  
  if (oee >= 85) return 'text-green-600'
  if (oee >= 65) return 'text-yellow-600'
  return 'text-red-600'
}